import { useMutation, useQuery } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import axios from '@/plugins/axios'
import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import { isStudentUser, permissions } from '@/services/ability'
import type { RegistrationStatusResponse } from '@/services/students'
import type { ApiResponse, User } from '@/types/global'

const requestOtp = async (email: string, password: string, otp?: string) => {
  const response = await axios.post<
    ApiResponse<User | { phone: string }> | { error: { code: number; message: string } }
  >('/api-proxy/web/session/authenticate', {
    jsonrpc: '2.0',
    params: { login: email, password: password, db: import.meta.env.VITE_API_DB, otp }
  })

  return response.data
}

const authenticate = async (email: string, password: string, otp: string) => {
  const response = await axios.post('/api-proxy/web/session/authenticate/advanced', {
    jsonrpc: '2.0',
    params: { login: email, password: password, db: import.meta.env.VITE_API_DB, otp: otp }
  })
  return response.data
}

const fetchCurrentUser = async () => {
  const response = await axios.post<ApiResponse<User>>('/api-proxy/web/session/get_session_info', {
    jsonrpc: '2.0',
    params: {}
  })
  return response.data
}

class AuthStore {
  user = ref<User | null>()

  init = async () => {
    try {
      const response = await fetchCurrentUser()
      this.user.value = response.result
    } catch (error) {
      this.user.value = null
    }
  }

  ability = computed(() => {
    return permissions(this.user.value)
  })

  isStudent = computed(() => {
    return isStudentUser(this.user.value)
  })

  useLogin = (
    handleRequestOtp: (phone: string) => void,
    handleError?: (message: string) => void,
    handleRegistrationStatus?: (data: RegistrationStatusResponse) => void,
    onAuthenticated?: () => Promise<void> | void
  ) =>
    useMutation({
      mutationKey: ['login'],
      retry: 3,
      mutationFn: async ({
        email,
        password,
        otp
      }: {
        email: string
        password: string
        otp?: string
      }) => {
        const params = {
          login: email,
          password: password,
          db: import.meta.env.VITE_API_DB
        }

        try {
          const data = await requestOtp(email, password, otp)

          return data
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data) => {
        if ('error' in data) {
          handleError?.(data.error.message)
          const toast = useToast()
          toast.error('حدث خطأ عند تسجيل الدخول')
          return
        }

        if ('phone' in data.result) {
          handleRequestOtp(data.result.phone)
          return
        }

        if (isRegistrationStatus(data.result)) {
          handleRegistrationStatus?.(data.result)
          return
        }

        this.user.value = data.result
        localStorage.user = JSON.stringify(data.result)
        sessionStorage.removeItem('otp_phone')
        sessionStorage.removeItem('otp_last_sent')

        sessionStorage.removeItem('otp_sent')
        localStorage.removeItem('selected-session-id')
        await onAuthenticated?.()
        const toast = useToast()
        toast.success('تم تسجيل الدخول بنجاح')
        return
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الدخول')
      }
    })

  useLogout = (onLoggedOut?: () => Promise<void> | void) =>
    useMutation({
      mutationKey: ['logout'],
      mutationFn: async () => {
        try {
          const response = await axios.post('/api-proxy/web/session/destroy', {
            jsonrpc: '2.0',
            params: {}
          })
          const result = response.data
          if (result) {
            localStorage.clear()
            this.user.value = null
          }
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async () => {
        const toast = useToast()
        await onLoggedOut?.()
        toast.success('تم تسجيل الخروج بنجاح')
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الخروج')
      }
    })
}

function isRegistrationStatus(value: unknown): value is RegistrationStatusResponse {
  if (!value || typeof value !== 'object') {
    return false
  }

  const state = (value as { state?: unknown }).state
  return state === 'draft' || state === 'in_progress' || state === 'cancel'
}

export const useUser = () =>
  useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.post<ApiResponse<User>>(
        '/api-proxy/web/session/get_session_info',
        {
          jsonrpc: '2.0',
          params: {}
        }
      )
      return response.data
    }
  })

export const auth = defineStore('auth', () => new AuthStore())
